"use client";

import { useEffect } from "react";
import {
  boundaryMonitoringContext,
  captureException,
} from "@/lib/error-monitoring.mjs";

/**
 * Root-layout error boundary. `app/error.tsx` cannot catch errors thrown by
 * `app/layout.tsx` itself; in Next.js App Router those bubble up to this
 * `global-error.tsx`, which replaces the entire document and therefore must
 * provide its own `<html>` and `<body>` tags.
 */
export default function GlobalError({
  error,
  unstable_retry,
}: {
  error: Error & { digest?: string };
  unstable_retry: () => void;
}) {
  useEffect(() => {
    console.error("[lenscoach] unhandled root-layout error", error);
    void captureException(
      error,
      boundaryMonitoringContext("global-error-boundary", error),
    );
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "4rem 1.5rem",
          textAlign: "center",
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        <h1 style={{ fontSize: "1.5rem", fontWeight: 600 }}>Something broke</h1>
        <p style={{ marginTop: "0.5rem", maxWidth: "28rem", color: "#6b7280" }}>
          An unexpected error occurred. Retry in a moment — if it keeps
          happening, reach out to support with this reference:
        </p>
        <code
          style={{
            marginTop: "0.5rem",
            padding: "0.25rem 0.5rem",
            borderRadius: "0.25rem",
            background: "#f3f4f6",
            fontSize: "0.75rem",
          }}
        >
          {error.digest ?? "no-digest"}
        </code>
        <button
          onClick={unstable_retry}
          style={{
            marginTop: "1.5rem",
            padding: "0.5rem 1rem",
            borderRadius: "0.375rem",
            background: "#111827",
            color: "white",
            border: "none",
            cursor: "pointer",
          }}
        >
          Try again
        </button>
      </body>
    </html>
  );
}
